import { createStore } from "zustand";

import type {
	Quiz,
	QuizInfo,
	QuizPopulated,
	Share,
} from "@vita-learning/types";

import {
	createQuiz,
	getQuiz,
	deleteQuiz,
	fetchQuizzes,
	sortQuizzesByCompletion,
	updateQuiz,
	createManyQuizzes,
	shareQuiz,
	createQuizShareUrl,
} from "~/shared/services";
import { partition } from "~/shared/utils";

export interface QuizzesState {
	readonly data: QuizPopulated[];
	readonly entity?: Quiz;
	readonly isPending: boolean;
}
interface QuizRemoveOptions {
	/**
	 * If true, performs a soft delete without removing the quiz from the store.
	 */
	readonly soft?: boolean;
}

interface QuizActions {
	/**
	 * Get a quiz by its ID.
	 * @param {string} id The ID of the quiz to retrieve.
	 * @returns {Promise<QuizPopulated>} The retrieved quiz.
	 */
	readonly get: (id: string) => Promise<QuizPopulated>;
	/**
	 * Refresh the quizzes list by fetching from the server.
	 * Bypasses cache and sorts quizzes by completion status.
	 * @param {string} url The URL endpoint to fetch quizzes from.
	 * @returns {Promise<QuizPopulated[]>} The refreshed and sorted list of quizzes.
	 */
	readonly refresh: (url: string) => Promise<QuizPopulated[]>;
	/**
	 * Create a new quiz.
	 * Adds the quiz to the store and sorts the list by completion status.
	 * @param {QuizInfo} quiz The quiz information to create.
	 * @returns {Promise<QuizPopulated>} The newly created quiz.
	 */
	readonly create: (quiz: QuizInfo) => Promise<QuizPopulated>;
	/**
	 * Create multiple quizzes in a single batch operation.
	 * Adds all quizzes to the store.
	 * @param {QuizInfo[]} quizzes An array of quiz information objects to create.
	 * @returns {Promise<QuizPopulated[]>} The newly created quizzes.
	 */
	readonly createMany: (quizzes: QuizInfo[]) => Promise<QuizPopulated[]>;
	/**
	 * Generate a shareable URL for a quiz.
	 * @param {string} id The ID of the quiz to create a share URL for.
	 * @returns {Promise<string>} The generated shareable URL.
	 */
	readonly createShareUrl: (id: string) => Promise<string>;
	/**
	 * Update an existing quiz.
	 * Updates both the entity state and the quiz in the data array, then re-sorts by completion.
	 * @param {QuizInfo | Quiz} quiz The quiz information or quiz object to update.
	 * @returns {Promise<QuizPopulated>} The updated quiz.
	 */
	readonly update: (quiz: QuizInfo | Quiz) => Promise<QuizPopulated>;
	/**
	 * Share a quiz and record the share metadata.
	 * Updates the quiz's share information in the store.
	 * @param {string} id The ID of the quiz to share.
	 * @param {Share} share The share metadata (e.g., timestamp, recipient info).
	 * @returns {Promise<QuizPopulated>} The quiz with updated share information.
	 */
	readonly share: (id: string, share: Share) => Promise<QuizPopulated>;
	/**
	 * Delete a quiz by its ID.
	 * Removes the quiz from the store and clears the entity state.
	 * @param {string} id The ID of the quiz to delete.
	 * @returns {Promise<QuizPopulated>} The deleted quiz.
	 */
	readonly remove: (
		id: string,
		options?: QuizRemoveOptions
	) => Promise<QuizPopulated>;
	/**
	 * Set or update the current entity state.
	 * Accepts either a quiz object or an updater function.
	 * @param {Quiz | ((prev: Quiz) => Quiz)} quiz The quiz to set, or a function that receives the previous quiz and returns the updated quiz.
	 */
	readonly set: (quiz: Quiz | ((prev: Quiz) => Quiz)) => void;
	/**
	 * Set the entity state to the quiz with the given ID.
	 * Finds the quiz in the data array and sets it as the current entity for editing.
	 * @param {string} id The ID of the quiz to edit.
	 */
	readonly edit: (id: string) => void;
	/**
	 * Reset the store to its default state.
	 * Clears all quizzes and entity data.
	 */
	readonly reset: () => void;
}

export type QuizzesStore = QuizzesState & QuizActions;

const defaultState: QuizzesState = {
	data: [],
	isPending: false,
};

export const createQuizzesStore = (initialState: QuizzesState) =>
	createStore<QuizzesStore>()((set, get) => ({
		...defaultState,
		...initialState,
		refresh: async url => {
			try {
				set({ isPending: true });
				const quizzes = await fetchQuizzes(url, { cache: "no-cache" });
				const sortedQuizzes = quizzes
					.toSorted(sortQuizzesByCompletion);

				set({ data: sortedQuizzes });
				return sortedQuizzes;
			} finally {
				set({ isPending: false });
			}
		},
		set: quizOrUpdater => {
			set(state => {
				if (!state.entity) return state;

				const nextEntity =
					typeof quizOrUpdater === "function"
						? quizOrUpdater(state.entity)
						: quizOrUpdater;
				return {
					...state,
					entity: nextEntity,
				};
			});
		},
		create: async quizInfo => {
			try {
				set({ isPending: true });
				const newQuiz = await createQuiz(quizInfo);

				set(state => ({
					...state,
					data: [...state.data, newQuiz].toSorted(sortQuizzesByCompletion),
				}));

				return newQuiz;
			} finally {
				set({ isPending: false });
			}
		},
		createMany: async quizzes => {
			try {
				set({ isPending: true });

				const newQuizzes = await createManyQuizzes(quizzes);

				set(state => ({ ...state, data: [...state.data, ...newQuizzes] }));
				return newQuizzes;
			} finally {
				set({ isPending: false });
			}
		},
		createShareUrl: async id => {
			try {
				set({ isPending: true });
				const { url } = await createQuizShareUrl({ id });

				return url;
			} finally {
				set({ isPending: false });
			}
		},
		get: async id => {
			try {
				set({ isPending: true });

				const quiz = await getQuiz(id, { cache: "no-cache" });

				return quiz;
			} finally {
				set({ isPending: false });
			}
		},
		update: async quizInfo => {
			try {
				set({ isPending: true });
				const updatedQuiz = await updateQuiz(quizInfo);

				set(state => ({
					...state,
					entity: state.entity ? updatedQuiz : state.entity,
					data: state.data
						.map(q => (q.id !== updatedQuiz.id ? q : updatedQuiz))
						.toSorted(sortQuizzesByCompletion),
				}));

				return updatedQuiz;
			} finally {
				set({ isPending: false });
			}
		},
		share: async (id, share) => {
			try {
				set({ isPending: true });
				const sharedQuiz = await shareQuiz(id, share);

				set(state => ({
					...state,
					entity: state.entity ? sharedQuiz : state.entity,
					data: state.data
						.map(q => (q.id !== sharedQuiz.id ? q : sharedQuiz))
						.toSorted(sortQuizzesByCompletion),
				}));
				return sharedQuiz;
			} finally {
				set({ isPending: false });
			}
		},
		remove: async (id, { soft = false } = { soft: false }) => {
			try {
				set({ isPending: true });
				await deleteQuiz(id);

				const [[deletedQuiz], restOfQuizzes] = partition(
					get().data,
					q => q.id === id
				);

				if (soft) return deletedQuiz as QuizPopulated;

				set({ data: restOfQuizzes, entity: undefined });

				return deletedQuiz as QuizPopulated;
			} finally {
				set({ isPending: false });
			}
		},
		edit: id => {
			set(state => ({
				...state,
				entity: state.data.find(q => q.id === id),
			}));
		},
		reset: () => set({ ...defaultState }),
	}));
